<template>
	<v-card elevation="1" height="100%" class="d-flex flex-column justify-space-around">
		<v-hover>
			<template #default="{isHovering, props}">
				<v-btn
					v-bind="props"
					id="microphone-btn"
					icon
					size="large"
					block
					variant="text"
					:class="{micOn: isMicOn}"
					rounded="0"
					:ripple="false"
					:disabled="
						!(
							((sequencerConnected || isSyndicationOriginated || isSplitMasterGoLiveDisabled) &&
								signalerIsConnected) ||
							(sipConnected && sipMicFeature)
						) ||
						userIsGuest ||
						systemInputDevices.length === 0
					"
					@click="toggleMic"
				>
					<div class="text-h3 font-weight-bold d-flex flex-column align-center">
						<v-icon
							:size="xxlScreen ? 68 : 32"
							class="pr-0"
							:class="{
								'mic-icon': isMicOn,
								'external-mic-icon': isExternalMicOn && !isMicOn,
								'sip-mic-icon': sipConnected && !userPeer?.muted && sipMicFeature
							}"
							style="--value: 0"
						>
							{{ micIcon(isHovering) }}
						</v-icon>
						{{ micLabel }}
					</div>
				</v-btn>
			</template>
		</v-hover>
		<AuditionAudio v-if="studioSettings"></AuditionAudio>
	</v-card>
</template>

<script>
/*Libraries*/
import * as _ from "lodash";

/* Modules */
import AuditionAudio from "@/components/AuditionAudio.vue";

/*Mixins*/
import SequencerPlayerStudioStatusMixin from "@/mixins/SequencerPlayerStudioStatus";

export default {
	name: "MicrophoneButton",

	components: {AuditionAudio},

	mixins: [SequencerPlayerStudioStatusMixin],

	inject: {
		goLivePanel: {default: null}
	},

	data() {
		return {
			context: null,
			analyser: null,
			volumeHeight: 0
		};
	},

	computed: {
		selectedStation() {
			return this.$store.getters.liveModeSelectedStation();
		},

		signalerIsConnected() {
			return this.$store.getters.isSignalerConnected();
		},

		isMicOn() {
			return this.$store.getters.isMicOn();
		},

		isExternalMicOn() {
			return this.$store.getters.getExternalMicOn();
		},

		systemInputDevices() {
			return this.$store.getters.systemInputDevices();
		},

		activeStudioInputDevice() {
			return this.$store.getters.getActiveStudioInputDevices()[0];
		},

		sipConnected() {
			return this.userPeer?.type === "SIP" && this.userPeer?.status === "CONNECTED";
		},

		micLabel() {
			if (this.sipConnected && this.sipMicFeature) {
				return `STUDIO ${this.userPeer?.muted ? "OFF" : "ON"}`;
			} else {
				return `MIC ${this.isMicOn || this.isExternalMicOn ? "ON" : "OFF"}`;
			}
		},

		sipMicFeature() {
			return (
				this.selectedStation &&
				this.$store.getters.isFeatureEnabled({
					object_id: this.selectedStation.StationID,
					reference: "zettacloud_live_console_manage_mute_studio"
				})
			);
		},

		xxlScreen() {
			return this.goLivePanel ? this.goLivePanel.xxl : this.$vuetify.display.xxl;
		}
	},

	watch: {
		volumeHeight(newVal) {
			const icon = document.querySelector(".mic-icon");
			if (icon) {
				icon.style.setProperty("--value", newVal);
			}
		},

		isMicOn(micOn) {
			this.updateBackgroundColor();
			if (!micOn) {
				this.volumeHeight = 0;
			}
		},

		activeStudioInputDevice(device, oldDevice) {
			if (!_.isEqual(device, oldDevice)) {
				this.$store.dispatch("setSignalerOutputStreamSource", {device, oldDevice}); //Action for changing the webRTC stream
				this.setAnalyser();
			}
		}
	},

	mounted() {
		this.setAnalyser();
	},

	beforeUnmount() {
		this.context.close();
	},

	methods: {
		updateBackgroundColor() {
			const bufferLength = this.analyser.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);
			this.analyser.getByteFrequencyData(dataArray);
			if (this.isMicOn) {
				this.volumeHeight = this.getVolume(dataArray);
				requestAnimationFrame(() => this.updateBackgroundColor());
			}
		},

		toggleMic() {
			if (!this.userIsGuest) {
				if (this.sipConnected) {
					this.$store.dispatch("livePlayerToggleStudioMicRequest", {
						playerKey: this.userPeer.playerKey,
						studio: this.userPeer.studio,
						muted: this.userPeer.muted
					});
				} else {
					this.$store.dispatch("toggleUserMic", {on: !this.isMicOn});
				}
			}
		},

		getVolume(dataArray) {
			const LOG_SCALE_FACTOR = 10;
			const LOG_SCALE_BASE = 1.5;
			const bufferLength = dataArray.length;
			let sum = 0;
			for (let i = 0; i < bufferLength; i++) {
				const frequency = dataArray[i];
				const logFrequency = Math.log(frequency + 1) / Math.log(LOG_SCALE_BASE);
				sum += logFrequency;
			}
			const averageLogFrequency = sum / bufferLength;
			return Math.pow(LOG_SCALE_BASE, averageLogFrequency) * LOG_SCALE_FACTOR;
		},

		setAnalyser() {
			let selectedDeviceId = this.activeStudioInputDevice?.device ?? "default";

			navigator.mediaDevices
				.getUserMedia({
					audio: selectedDeviceId !== "default" ? {deviceId: {exact: selectedDeviceId}} : true
				})
				.then(stream => {
					if (this.context) {
						this.context.close();
					}
					this.context = new AudioContext();
					this.analyser = this.context.createAnalyser();
					this.analyser.fftSize = 2048;
					const source = this.context.createMediaStreamSource(stream);
					source.connect(this.analyser);
				})
				.catch(error => {
					console.error("getUserMedia error: ", error);
				});
		},

		micIcon(isHovering) {
			if (this.sipConnected) {
				if (this.userPeer?.muted) {
					return isHovering ? "mdi-microphone" : "mdi-microphone-off";
				} else {
					return isHovering ? "mdi-microphone-off" : "mdi-microphone";
				}
			} else {
				return "mdi-microphone";
			}
		}
	}
};
</script>

<style lang="scss" scoped>
$value: var(--value);

.mic-icon {
	background: linear-gradient(
		to top,
		#28a745 0%,
		#28a745 calc(#{$value} * 1%),
		$grey-300 calc(#{$value} * 1%),
		$grey-300 100%
	);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}

.external-mic-icon {
	background: $ihm-red;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}

.sip-mic-icon {
	background-color: $ihm-green;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}

.v-card {
	transition: background 0.5s;

	#microphone-btn {
		letter-spacing: 0.02em !important;

		&::before {
			background-color: transparent !important;
		}

		.v-icon {
			color: $grey-300 !important;
		}

		&.micOn {
			background: $ihm-red;
			color: $ihm-white;
		}

		&:hover {
			.v-icon {
				opacity: 0.5;
			}
		}
	}

	.v-icon {
		transition: 0s;
		--value: 0;
	}
}
</style>
